<!--
  BaseActionButtons

  Shared action column buttons for list views: edit, duplicate, details, delete.
  Handles permissions (BaseUserCan), RouterLink vs button, and busy states.

  Design decisions:
  - Presence of a prop controls visibility — omit to hide a button entirely
  - `to` on any action renders it as RouterLink (preserves browser link semantics)
  - `label` on edit/duplicate/delete switches from icon-only circle to text+icon pill
  - Details always renders as text+caret label button (label defaults to 'Details')
  - Top-level `isBusy` disables all buttons; per-action `isBusy` disables individually

  Out of scope:
  - Actions with complex internal logic (e.g. Download with API call) — keep those in parent
  - Confirmation should be handle by global app confirm in parent action handler
-->
<template>
  <div class="d-flex align-items-center gap-2">
    <!-- Edit -->
    <template v-if="edit && !edit.hidden">
      <BaseUserCan v-if="edit.permission" :permission="edit.permission">
        <ATooltip :title="edit.label ?? t('Edit')">
          <component
            :is="edit.to ? RouterLink : 'button'"
            v-bind="edit.to ? { to: edit.to } : { type: 'button' }"
            :class="actionButtonClass(edit, 'primary')"
            :disabled="isActionBusy(edit) || undefined"
            @click="handleActionClick('edit')"
          >
            <template v-if="edit.label !== undefined">
              <span class="me-1">{{ edit.label }}</span>
              <BasePortalIcon :icon="edit.icon ?? 'edit'" height="1em" width="1em" />
            </template>
            <BasePortalIcon
              v-else
              :icon="edit.icon ?? 'edit'"
              :class="`text-${baseColor(edit, 'primary')}`"
            />
          </component>
        </ATooltip>
      </BaseUserCan>
      <ATooltip v-else :title="edit.label ?? t('Edit')">
        <component
          :is="edit.to ? RouterLink : 'button'"
          v-bind="edit.to ? { to: edit.to } : { type: 'button' }"
          :class="actionButtonClass(edit, 'primary')"
          :disabled="isActionBusy(edit) || undefined"
          @click="handleActionClick('edit')"
        >
          <template v-if="edit.label !== undefined">
            <span class="me-1">{{ edit.label }}</span>
            <BasePortalIcon :icon="edit.icon ?? 'edit'" height="1em" width="1em" />
          </template>
          <BasePortalIcon
            v-else
            :icon="edit.icon ?? 'edit'"
            :class="`text-${baseColor(edit, 'primary')}`"
          />
        </component>
      </ATooltip>
    </template>

    <!-- Duplicate -->
    <template v-if="duplicate && !duplicate.hidden">
      <BaseUserCan v-if="duplicate.permission" :permission="duplicate.permission">
        <ATooltip :title="duplicate.tooltip ?? t('Duplicate')">
          <component
            :is="duplicate.to ? RouterLink : 'button'"
            v-bind="duplicate.to ? { to: duplicate.to } : { type: 'button' }"
            :class="actionButtonClass(duplicate, 'primary')"
            :disabled="isActionBusy(duplicate) || undefined"
            @click="handleActionClick('duplicate')"
          >
            <template v-if="duplicate.label !== undefined">
              <span class="me-1">{{ duplicate.label }}</span>
              <BasePortalIcon :icon="duplicate.icon ?? 'copy'" height="1em" width="1em" />
            </template>
            <BasePortalIcon
              v-else
              :icon="duplicate.icon ?? 'copy'"
              :class="`text-${baseColor(duplicate, 'primary')}`"
            />
          </component>
        </ATooltip>
      </BaseUserCan>
      <ATooltip v-else :title="duplicate.label ?? t('Duplicate')">
        <component
          :is="duplicate.to ? RouterLink : 'button'"
          v-bind="duplicate.to ? { to: duplicate.to } : { type: 'button' }"
          :class="actionButtonClass(duplicate, 'primary')"
          :disabled="isActionBusy(duplicate) || undefined"
          @click="handleActionClick('duplicate')"
        >
          <template v-if="duplicate.label !== undefined">
            <span class="me-1">{{ duplicate.label }}</span>
            <BasePortalIcon :icon="duplicate.icon ?? 'copy'" height="1em" width="1em" />
          </template>
          <BasePortalIcon
            v-else
            :icon="duplicate.icon ?? 'copy'"
            :class="`text-${baseColor(duplicate, 'primary')}`"
          />
        </component>
      </ATooltip>
    </template>

    <!-- Details: always renders as text+caret label button -->
    <template v-if="details && !details.hidden">
      <BaseUserCan v-if="details.permission" :permission="details.permission">
        <component
          :is="details.to ? RouterLink : 'button'"
          v-bind="details.to ? { to: details.to } : { type: 'button' }"
          :class="labelButtonClass(details, 'primary')"
          :disabled="isActionBusy(details) || undefined"
          @click="handleActionClick('details')"
        >
          <span class="d-inline-flex me-1">{{ details.label ?? t('Details') }}</span>
          <BasePortalIcon :icon="details.icon ?? 'caret'" height="0.5em" width="0.5em" />
        </component>
      </BaseUserCan>
      <component
        :is="details.to ? RouterLink : 'button'"
        v-else
        v-bind="details.to ? { to: details.to } : { type: 'button' }"
        :class="labelButtonClass(details, 'primary')"
        :disabled="isActionBusy(details) || undefined"
        @click="handleActionClick('details')"
      >
        <span class="d-inline-flex me-1">{{ details.label ?? t('Details') }}</span>
        <BasePortalIcon :icon="details.icon ?? 'caret'" height="0.5em" width="0.5em" />
      </component>
    </template>

    <!-- Delete: always a button, never a RouterLink -->
    <template v-if="props.delete && !props.delete.hidden">
      <BaseUserCan v-if="props.delete.permission" :permission="props.delete.permission">
        <ATooltip :title="props.delete.label ?? t('Delete')">
          <button
            type="button"
            :class="actionButtonClass(props.delete, 'danger')"
            :disabled="isActionBusy(props.delete) || undefined"
            @click="handleActionClick('delete')"
          >
            <template v-if="props.delete.label !== undefined">
              <span class="me-1">{{ props.delete.label }}</span>
              <BasePortalIcon :icon="props.delete.icon ?? 'trash'" height="1em" width="1em" />
            </template>
            <BasePortalIcon
              v-else
              :icon="props.delete.icon ?? 'trash'"
              :class="`text-${baseColor(props.delete, 'danger')}`"
            />
          </button>
        </ATooltip>
      </BaseUserCan>
      <ATooltip v-else :title="props.delete.label ?? t('Delete')">
        <button
          type="button"
          :class="actionButtonClass(props.delete, 'danger')"
          :disabled="isActionBusy(props.delete) || undefined"
          @click="handleActionClick('delete')"
        >
          <template v-if="props.delete.label !== undefined">
            <span class="me-1">{{ props.delete.label }}</span>
            <BasePortalIcon :icon="props.delete.icon ?? 'trash'" height="1em" width="1em" />
          </template>
          <BasePortalIcon
            v-else
            :icon="props.delete.icon ?? 'trash'"
            :class="`text-${baseColor(props.delete, 'danger')}`"
          />
        </button>
      </ATooltip>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { useI18n } from 'vue-i18n'
import { RouterLink, type RouteLocationRaw } from 'vue-router'
import { asyncComponentWrapper } from '@/composables/useReloadModal'

const ATooltip = asyncComponentWrapper(() => import('ant-design-vue/es/tooltip'))

export type ActionConfig = {
  permission?: string | string[]
  to?: RouteLocationRaw
  icon?: string
  color?: string
  label?: string
  tooltip?: string
  isBusy?: boolean
  hidden?: boolean
}

type ActionKey = 'details' | 'edit' | 'duplicate' | 'delete'

const props = defineProps<{
  details?: ActionConfig
  edit?: ActionConfig
  duplicate?: ActionConfig
  delete?: ActionConfig
  isBusy?: boolean
}>()

const emit = defineEmits<{
  (e: 'details'): void
  (e: 'edit'): void
  (e: 'duplicate'): void
  (e: 'delete'): void
}>()

const { t } = useI18n()

// Strips 'outline-' prefix to get the base Bootstrap color for text/icon classes
const baseColor = (action: ActionConfig, defaultColor: string) =>
  (action.color ?? defaultColor).replace('outline-', '')

const isActionBusy = (action: ActionConfig) => props.isBusy || action.isBusy

// Icon-only circle button
const iconButtonClass = (action: ActionConfig, defaultColor: string) => {
  const color = action.color ?? defaultColor
  return `btn btn-${color} btn-transparent-${color} border-0 rounded-pill p-0 rounded-circle d-inline-flex align-items-center justify-content-center my-1`
}

// Text + icon pill button
const labelButtonClass = (action: ActionConfig, defaultColor: string) => {
  const color = action.color ?? defaultColor
  return `btn btn-${color} rounded-pill btn-sm d-inline-flex align-items-center`
}

// Switches between icon-only and label styles based on whether label is set
const actionButtonClass = (action: ActionConfig, defaultColor: string) =>
  action.label !== undefined
    ? labelButtonClass(action, defaultColor)
    : iconButtonClass(action, defaultColor)

const handleActionClick = (key: ActionKey) => {
  const action = props[key]
  if (!action || action.to) return // RouterLink handles its own navigation

  if (key === 'details') emit('details')
  if (key === 'edit') emit('edit')
  if (key === 'duplicate') emit('duplicate')
  if (key === 'delete') emit('delete')
}
</script>
